import type { QuoteScreenPrintResult, ResolvedGarmentSummary } from '@fsp/contracts';
import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import {
  type ApiErrorBody,
  type QuoteError,
  quoteLabel,
  toApiInput,
  totalQty,
  UiSchema,
  type UiValues,
} from './_shared';
import { pushCalcRecent } from './calc-recents';
import { pushRecent } from './recents';

export type LiveQuoteState =
  | { status: 'empty' }
  | { status: 'calculating' }
  | {
      status: 'ready';
      result: QuoteScreenPrintResult;
      resolvedGarment?: ResolvedGarmentSummary;
    }
  | { status: 'error'; error: QuoteError };

export type UseLiveQuoteOptions = {
  /** Milliseconds to wait after the last change before firing. Default 300. */
  debounceMs?: number;
};

/**
 * Fires a POST /quote/screen-print whenever `values` changes and passes
 * validation. Debounced, with payload-dedupe so identical inputs don't
 * re-hit the API on unrelated re-renders.
 *
 * Request volume for a full quote flow: ~3–8 calls (one per meaningful
 * field change), each on a 300 ms debounce. Acceptable for a 3-user
 * internal tool on Lambda; the 4a audit already confirmed the API is
 * fast + the token refresh is silent.
 */
export function useLiveQuote(values: UiValues, options: UseLiveQuoteOptions = {}): LiveQuoteState {
  const { debounceMs = 300 } = options;
  const client = useApiClient();
  const [state, setState] = useState<LiveQuoteState>({ status: 'empty' });
  const lastPayloadRef = useRef<string | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const parsed = UiSchema.safeParse(values);
    if (!parsed.success) {
      // Invalid form state — don't fire, but also don't clobber a
      // previously-ready result if the user is mid-edit. Keep whatever
      // the panel was already showing until the next valid payload.
      return;
    }

    const body = toApiInput(parsed.data);
    const payloadKey = JSON.stringify(body);
    if (payloadKey === lastPayloadRef.current) return;

    const timer = setTimeout(() => {
      lastPayloadRef.current = payloadKey;
      inFlightRef.current?.abort();
      const abort = new AbortController();
      inFlightRef.current = abort;
      setState({ status: 'calculating' });

      (async () => {
        try {
          const { data, error, response } = await client.POST('/quote/screen-print', {
            body,
            signal: abort.signal,
          });
          if (abort.signal.aborted) return;
          if (error || !data) {
            const envelope = error as { error?: ApiErrorBody } | undefined;
            const quoteError: QuoteError = {
              status: response.status,
              body: envelope?.error ?? {
                type: 'unknown',
                title: 'Unknown error',
                detail: `HTTP ${response.status}`,
              },
            };
            setState({ status: 'error', error: quoteError });
            return;
          }
          // `data.data` is typed from the generated client, which models
          // the response as the engine result plus an optional
          // `resolvedGarment`. We split it here so the panel can read
          // the two fields without `any`-casting.
          const { resolvedGarment, ...rest } = data.data as QuoteScreenPrintResult & {
            resolvedGarment?: ResolvedGarmentSummary;
          };
          setState({
            status: 'ready',
            result: rest as QuoteScreenPrintResult,
            resolvedGarment,
          });
          // Push SS Activewear picks to the recents list so they surface
          // under Quick picks on the next quote. Guard on both fields so
          // we don't store partial state (e.g. style set but color empty).
          if (
            parsed.data.garmentSource === 'we_provide_by_style' &&
            parsed.data.style.trim() &&
            parsed.data.colorName.trim()
          ) {
            pushRecent({
              style: parsed.data.style.trim(),
              colorName: parsed.data.colorName.trim(),
            });
          }
          // Push the full calc to the Recent-calculations history so the
          // result panel's popover can restore it. Dedupe lives inside
          // pushCalcRecent — repeated saves of the same inputs refresh
          // the timestamp instead of stacking rows.
          pushCalcRecent({
            at: Date.now(),
            values: parsed.data,
            summary: {
              label: quoteLabel(parsed.data),
              perUnitSell: data.data.perUnitSell,
              orderTotal: data.data.orderTotal,
              marginPct: data.data.marginPct,
              totalQty: totalQty(parsed.data),
              mode: parsed.data.mode,
            },
          });
        } catch (err) {
          if (abort.signal.aborted) return;
          const quoteError: QuoteError = {
            status: 0,
            body: {
              type: 'network_error',
              title: 'Could not reach the API',
              detail:
                err instanceof Error ? err.message : 'Request failed before reaching the server.',
            },
          };
          setState({ status: 'error', error: quoteError });
        }
      })();
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [values, client, debounceMs]);

  return state;
}
